import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Trash2, Pin, ChevronLeft, X } from 'lucide-react';

const ProfessionalChatHistory = ({
  onSelectChat,
  firestoreService,
  currentSessionId
}) => {
  const [filterPinned, setFilterPinned] = useState('all'); // ✅ ADD THIS
  const [allSessions, setAllSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clearAllConfirm, setClearAllConfirm] = useState(null); // NEW: Clear all confirmation
  const [sortBy, setSortBy] = useState('recent');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [filterDay, setFilterDay] = useState('all'); // 'all', 'Monday', 'Tuesday', etc.
const [sortWeekBy, setSortWeekBy] = useState('recent'); // 'recent', 'oldest'
  const dropdownRef = useRef(null);
  const [showSortWeekDropdown, setShowSortWeekDropdown] = useState(false);
const [showDayDropdown, setShowDayDropdown] = useState(false);
const sortWeekDropdownRef = useRef(null);
const dayDropdownRef = useRef(null);

  const folderColors = ['#6b7280', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
useEffect(() => {
  const handleClickOutside = (event) => {
    if (sortWeekDropdownRef.current && !sortWeekDropdownRef.current.contains(event.target)) {
      setShowSortWeekDropdown(false);
    }
    if (dayDropdownRef.current && !dayDropdownRef.current.contains(event.target)) {
      setShowDayDropdown(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);


  useEffect(() => {
    loadChatSessions();
  }, [firestoreService]);

  const convertFirestoreDate = (dateValue) => {
    if (!dateValue) {
      console.warn('⚠️ No date value provided, using current time');
      return new Date();
    }

    if (dateValue && typeof dateValue === 'object' && dateValue.toDate && typeof dateValue.toDate === 'function') {
      try {
        const converted = dateValue.toDate();
        if (!isNaN(converted.getTime())) {
          return converted;
        }
      } catch (e) {
        console.warn('⚠️ Failed to convert Firestore Timestamp:', e);
      }
    }

    if (typeof dateValue === 'string') {
      try {
        const parsed = new Date(dateValue);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch (e) {
        console.warn('⚠️ Failed to parse ISO string:', dateValue);
      }
    }

    if (typeof dateValue === 'number') {
      try {
        const parsed = new Date(dateValue);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch (e) {
        console.warn('⚠️ Failed to parse timestamp:', dateValue);
      }
    }

    if (dateValue instanceof Date) {
      if (!isNaN(dateValue.getTime())) {
        return dateValue;
      }
    }

    console.warn('⚠️ Unknown date format, using current time:', typeof dateValue);
    return new Date();
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const getCurrentWeekOfMonth = () => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const offsetDate = now.getDate() + firstDayOfWeek - 1;
    return Math.floor(offsetDate / 7) + 1;
  };

  const getWeekOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();

    const adjustedDate = day + firstDayOfWeek - 1;
    const weekNumber = Math.floor(adjustedDate / 7) + 1;

    return Math.min(weekNumber, 5);
  };

  const getAllWeeksInMonth = (monthYear) => {
    const [monthName, year] = monthYear.split(' ');
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
    const yearNum = parseInt(year);

    const lastDay = new Date(yearNum, monthIndex + 1, 0).getDate();
    const lastDayWeek = getWeekOfMonth(new Date(yearNum, monthIndex, lastDay));

    const weeks = {};
    for (let i = 1; i <= lastDayWeek; i++) {
      weeks[`Week ${i}`] = [];
    }

    return weeks;
  };

  const formatTime = (timestamp) => {
    try {
      const date = convertFirestoreDate(timestamp);
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('⚠️ Error formatting time:', error);
      return 'Invalid date';
    }
  };

  const loadChatSessions = async () => {
    if (!firestoreService?.isReady?.()) {
      console.log('⏳ Firestore not ready');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('📂 Loading chat sessions...');
      
      const rawSessions = await firestoreService.getAllChatSessions(100);
      
      console.log('📊 Raw sessions received:', rawSessions.length);

      const uniqueSessions = Array.from(
        new Map(rawSessions.map(s => [s.id, s])).values()
      );
      
      const validatedSessions = uniqueSessions.filter(session => {
        try {
          const dateValue = session.lastUpdated || session.timestamp || session.savedAt;
          
          if (!dateValue) {
            console.warn('⚠️ Session has no date field, skipping:', session.id);
            return false;
          }

          const convertedDate = convertFirestoreDate(dateValue);
          
          if (isNaN(convertedDate.getTime())) {
            console.warn('⚠️ Invalid date after conversion, skipping:', session.id);
            return false;
          }

          return true;
        } catch (error) {
          console.warn('⚠️ Error validating session date:', session.id, error);
          return false;
        }
      });

      if (validatedSessions.length < uniqueSessions.length) {
        console.warn(`⚠️ Filtered out ${uniqueSessions.length - validatedSessions.length} invalid sessions`);
      }
      
      const sorted = validatedSessions.sort((a, b) => {
        try {
          const dateA = convertFirestoreDate(a.lastUpdated || a.timestamp || a.savedAt);
          const dateB = convertFirestoreDate(b.lastUpdated || b.timestamp || b.savedAt);
          return dateB - dateA;
        } catch (e) {
          console.warn('⚠️ Sort error:', e);
          return 0;
        }
      });
      
      setAllSessions(sorted);
      console.log('✅ Loaded', sorted.length, 'valid sessions');
      
    } catch (error) {
      console.error('❌ Failed to load sessions:', error);
      setAllSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const organizedSessions = useMemo(() => {
    const organized = {};
    
    allSessions.forEach(session => {
      try {
        const dateValue = session.lastUpdated || session.timestamp || session.savedAt;
        const date = convertFirestoreDate(dateValue);

        if (isNaN(date.getTime())) {
          console.warn('⚠️ Skipping session with invalid date:', session.id);
          return;
        }
        
        const monthYear = date.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        });

        const weekNum = getWeekOfMonth(date);
        const weekLabel = `Week ${weekNum}`;

        if (!organized[monthYear]) {
          organized[monthYear] = {};
        }

        if (!organized[monthYear][weekLabel]) {
          organized[monthYear][weekLabel] = [];
        }

        organized[monthYear][weekLabel].push(session);

      } catch (error) {
        console.error('❌ Error organizing session:', session.id, error.message);
      }
    });

    return organized;
  }, [allSessions]);

  // ✅ FIXED: Proper filtering AND sorting
const filteredAndSortedSessions = useMemo(() => {
  let sessions = searchQuery.trim() 
    ? allSessions.filter(session => 
        session.preview?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.messages?.some(msg => 
          msg.content?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : allSessions;

  // ✅ ADD: Filter by pinned status
  if (filterPinned === 'pinned') {
    sessions = sessions.filter(s => s.pinned === true);
  } else if (filterPinned === 'unpinned') {
    sessions = sessions.filter(s => !s.pinned);
  }

  // Apply sorting
  return [...sessions].sort((a, b) => {
    // ✅ Always show pinned first regardless of sort
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    
    if (sortBy === 'recent') {
      const dateA = convertFirestoreDate(a.lastUpdated || a.timestamp || a.savedAt);
      const dateB = convertFirestoreDate(b.lastUpdated || b.timestamp || b.savedAt);
      return dateB - dateA;
    } else if (sortBy === 'oldest') {
      const dateA = convertFirestoreDate(a.lastUpdated || a.timestamp || a.savedAt);
      const dateB = convertFirestoreDate(b.lastUpdated || b.timestamp || b.savedAt);
      return dateA - dateB;
    } else if (sortBy === 'messages') {
      return (b.messageCount || 0) - (a.messageCount || 0);
    }
    return 0;
  });
}, [allSessions, searchQuery, sortBy, filterPinned]); // ✅ ADD filterPinned dependency
  const handleSelectSession = (session) => {
    console.log('🔐 Selected session:', {
      id: session.id,
      preview: session.preview,
      messageCount: session.messageCount
    });
    
    onSelectChat(session.id);
  };
const handleDeleteSession = async (sessionId) => {
  if (!firestoreService?.isReady?.()) {
    console.error('❌ Firestore not ready');
    alert('Unable to delete: Firestore not initialized');
    return;
  }

  try {
    console.log('🗑️ Attempting to delete session:', sessionId);

    // ✅ Call the fixed deletion method
    await firestoreService.deleteChatSession(sessionId);
    
    // ✅ Update local state IMMEDIATELY (no reload needed)
    setAllSessions(prev => prev.filter(s => s.id !== sessionId));
    setDeleteConfirm(null);
    
    console.log('✅ Session deleted and UI updated');
    
  } catch (error) {
    console.error('❌ Failed to delete session:', error);
    alert(`Failed to delete session: ${error.message}`);
  }
};

  // ✅ NEW: Clear all conversations
const handleClearAll = async () => {
  if (!firestoreService?.isReady?.()) {
    console.error('❌ Firestore not ready');
    alert('Unable to clear: Firestore not initialized');
    return;
  }

  try {
    let result;
    let deletedCount = 0;

    if (expandedMonth && expandedWeek) {
      // ✅ Clear specific week
      console.log(`🗑️ Clearing ${expandedWeek} in ${expandedMonth}...`);
      
      const { startDate, endDate } = firestoreService.getDateRangeForPeriod(
        expandedMonth, 
        expandedWeek
      );
      
      result = await firestoreService.deleteChatSessionsByDateRange(
        startDate,
        endDate,
        `${expandedWeek} in ${expandedMonth}`
      );
      
      deletedCount = result.deletedCount;

    } else if (expandedMonth) {
      // ✅ Clear entire month
      console.log(`🗑️ Clearing entire month: ${expandedMonth}...`);
      
      const { startDate, endDate } = firestoreService.getDateRangeForPeriod(expandedMonth);
      
      result = await firestoreService.deleteChatSessionsByDateRange(
        startDate,
        endDate,
        expandedMonth
      );
      
      deletedCount = result.deletedCount;

    } else if (searchQuery) {
      // ✅ Clear search results
      console.log('🗑️ Clearing search results...');
      
      const sessionIds = filteredAndSortedSessions.map(s => s.id);
      result = await firestoreService.deleteChatSessions(sessionIds);
      deletedCount = result.deletedCount;

    } else {
      // ✅ Clear ALL conversations
      console.log('🗑️ Clearing ALL conversations...');
      
      result = await firestoreService.clearAllConversations();
      deletedCount = result.deletedCount;
    }

    // ✅ Update local state IMMEDIATELY
    if (result.success) {
      const deletedIds = new Set(result.sessionIds || []);
      setAllSessions(prev => prev.filter(s => !deletedIds.has(s.id)));
      setClearAllConfirm(null);
      
      // ✅ Navigate back if in nested view
      if (expandedWeek) {
        setExpandedWeek(null);
      } else if (expandedMonth) {
        setExpandedMonth(null);
      }

      console.log(`✅ Successfully cleared ${deletedCount} conversations`);
      
      // Optional: Show success message
      // alert(`Successfully deleted ${deletedCount} conversation${deletedCount !== 1 ? 's' : ''}`);
    }

  } catch (error) {
    console.error('❌ Failed to clear conversations:', error);
    alert(`Failed to clear conversations: ${error.message}`);
  }
};


const handleTogglePin = async (e, sessionId) => {
  e.stopPropagation();
  e.preventDefault();

  if (!firestoreService?.isReady?.()) {
    console.error('❌ Firestore not ready');
    alert('Unable to pin: Firestore not initialized');
    return;
  }

  try {
    console.log('📌 Toggling pin for session:', sessionId);
    
    const session = allSessions.find(s => s.id === sessionId);
    
    if (!session) {
      console.error('❌ Session not found:', sessionId);
      return;
    }

    const newPinnedState = !session.pinned;
    
    // ✅ FIXED: Use setDoc with merge instead of updateDoc
    // This will create the document if it doesn't exist
    try {
      if (typeof firestoreService.updateChatSessionMetadata === 'function') {
        await firestoreService.updateChatSessionMetadata(sessionId, {
          pinned: newPinnedState
        });
      } else if (typeof firestoreService.saveConversation === 'function') {
        // If updateChatSessionMetadata doesn't exist, use saveConversation
        await firestoreService.saveConversation({
          id: sessionId,
          ...session,
          pinned: newPinnedState
        });
      } else {
        alert('Pin function not available. Check console for details.');
        return;
      }
    } catch (error) {
      // ✅ If document doesn't exist, create it with saveConversation
      if (error.code === 'not-found' && typeof firestoreService.saveConversation === 'function') {
        console.log('📝 Document not found, creating with pinned state...');
        await firestoreService.saveConversation({
          id: sessionId,
          ...session,
          pinned: newPinnedState
        });
      } else {
        throw error;
      }
    }
    
    setAllSessions(prev => prev.map(s => 
      s.id === sessionId ? { ...s, pinned: newPinnedState } : s
    ));

    console.log('✅ Pin toggled successfully:', { sessionId, pinned: newPinnedState });
    
  } catch (error) {
    console.error('❌ Failed to toggle pin:', error);
    alert(`Failed to pin session: ${error.message}`);
  }
};

  const FolderIcon = ({ color }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  );

  // ✅ NEW: Get context-specific clear all text
const getClearAllText = () => {
  if (expandedMonth && expandedWeek) {
    const sessions = organizedSessions[expandedMonth]?.[expandedWeek] || [];
    const count = sessions.length;
    return {
      title: `Clear ${expandedWeek}?`,
      description: `This will permanently delete ${count} conversation${count !== 1 ? 's' : ''} from ${expandedWeek} in ${expandedMonth}. This action cannot be undone.`,
      count
    };
  } else if (expandedMonth) {
    const weeks = organizedSessions[expandedMonth] || {};
    const count = Object.values(weeks).flat().length;
    return {
      title: `Clear ${expandedMonth}?`,
      description: `This will permanently delete ${count} conversation${count !== 1 ? 's' : ''} from ${expandedMonth}. This action cannot be undone.`,
      count
    };
  } else if (searchQuery) {
    const count = filteredAndSortedSessions.length;
    return {
      title: 'Clear Search Results?',
      description: `This will permanently delete ${count} conversation${count !== 1 ? 's' : ''} from your search results. This action cannot be undone.`,
      count
    };
  } else {
    return {
      title: 'Clear All Conversations?',
      description: `This will permanently delete all ${allSessions.length} conversation${allSessions.length !== 1 ? 's' : ''}. This action cannot be undone.`,
      count: allSessions.length
    };
  }
};

  // Week View
  if (expandedMonth && expandedWeek && !searchQuery) {
    const sessions = organizedSessions[expandedMonth]?.[expandedWeek] || [];
const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Get unique days in this week
const daysInWeek = [...new Set(sessions.map(s => {
  const d = convertFirestoreDate(s.lastUpdated || s.timestamp || s.savedAt);
  return DAY_NAMES[d.getDay()];
}))];

// Filter by selected day
const daySessions = filterDay === 'all' 
  ? sessions 
  : sessions.filter(s => {
      const d = convertFirestoreDate(s.lastUpdated || s.timestamp || s.savedAt);
      return DAY_NAMES[d.getDay()] === filterDay;
    });

// Sort
const sortedSessions = [...daySessions].sort((a, b) => {
  if (a.pinned && !b.pinned) return -1;
  if (!a.pinned && b.pinned) return 1;
  const dateA = convertFirestoreDate(a.lastUpdated || a.timestamp);
  const dateB = convertFirestoreDate(b.lastUpdated || b.timestamp);
  return sortWeekBy === 'oldest' ? dateA - dateB : dateB - dateA;
});

// Group by day
const groupedByDay = {};
sortedSessions.forEach(session => {
  const d = convertFirestoreDate(session.lastUpdated || session.timestamp || session.savedAt);
  const dayName = DAY_NAMES[d.getDay()];
  if (!groupedByDay[dayName]) groupedByDay[dayName] = [];
  groupedByDay[dayName].push(session);
});

    return (
      <>
        <style>{styles}</style>
        <div className="chats-history-chat-history-container">
          <div className="chats-history-chat-history-inner">
            <div className="chats-history-top-bar">
              <button
                onClick={() => setExpandedWeek(null)}
                className="chats-history-back-button"
              >
                <ChevronLeft size={18} />
              </button>

              {sessions.length > 0 && (
 <button
  onClick={() => setClearAllConfirm(true)}
  className="chats-history-clear-all-btn"
>
  Clear {expandedWeek}
</button>
              )}
            </div>

            <div className="chats-history-section-header">
              <h2 className="chats-history-section-title">{expandedMonth} - {expandedWeek}</h2>
            </div>
{/* Filter bar */}
<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>

  {/* Sort dropdown — same pattern as main view */}
  <div className="chats-custom-dropdown" ref={sortWeekDropdownRef}>
    <button
      className="chats-dropdown-button"
      onClick={() => setShowSortWeekDropdown(!showSortWeekDropdown)}
    >
      {sortWeekBy === 'recent' ? 'Most Recent' : 'Oldest First'}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
    {showSortWeekDropdown && (
      <div className="chats-dropdown-menu">
        {['recent', 'oldest'].map(option => (
          <div
            key={option}
            className={`chats-dropdown-item ${sortWeekBy === option ? 'active' : ''}`}
            onClick={() => { setSortWeekBy(option); setShowSortWeekDropdown(false); }}
          >
            {option === 'recent' ? 'Most Recent' : 'Oldest First'}
            {sortWeekBy === option && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        ))}
      </div>
    )}
  </div>

  <div style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />

  {/* Day filter dropdown — same pattern */}
  <div className="chats-custom-dropdown" ref={dayDropdownRef}>
    <button
      className="chats-dropdown-button"
      onClick={() => setShowDayDropdown(!showDayDropdown)}
    >
      {filterDay === 'all' ? 'All Days' : filterDay}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
    {showDayDropdown && (
      <div className="chats-dropdown-menu">
        {['all', ...daysInWeek].map(day => (
          <div
            key={day}
            className={`chats-dropdown-item ${filterDay === day ? 'active' : ''}`}
            onClick={() => { setFilterDay(day); setShowDayDropdown(false); }}
          >
            {day === 'all' ? 'All Days' : day}
            {filterDay === day && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        ))}
      </div>
    )}
  </div>

</div>
<div className="chats-history-chat-list-container">
  {sortedSessions.length === 0 ? (
    <div className="chats-history-empty-state">
      <p style={{ color: '#9ca3af' }}>No sessions in this week</p>
    </div>
  ) : (
    Object.entries(groupedByDay).map(([dayName, daySessions]) => (
      <div key={dayName} style={{ marginBottom: '24px' }}>
        {/* Day header */}
<div style={{
  fontSize: '13px',
  fontWeight: 600,
  color: '#3b82f6',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  marginBottom: '10px',
  paddingLeft: '4px',
}}>
  {dayName}
</div>

        {/* Sessions under this day */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {daySessions.map((session) => (
            <div
              key={session.id}
              className={`chats-history-chat-item-card ${session.pinned ? 'chats-pinned' : ''}`}
            >
              <div
                className="chats-history-chat-item-inner"
                onClick={() => handleSelectSession(session)}
              >
                <div className="chats-history-chat-preview">{session.preview || 'New Chat'}</div>
                <div className="chats-history-chat-meta">
                  <span>{formatTime(session.lastUpdated || session.timestamp)}</span>
                  <span className="chats-history-meta-divider">•</span>
                  <span>{session.messageCount || 0} msg{session.messageCount !== 1 ? 's' : ''}</span>
                </div>
              </div>

              <div className="chats-action-buttons">
                <button
                  onClick={(e) => handleTogglePin(e, session.id)}
                  className={`chats-icon-btn ${session.pinned ? 'active' : ''}`}
                  title={session.pinned ? 'Unpin' : 'Pin'}
                >
                  <Pin size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDeleteConfirm(session.id);
                  }}
                  className="chats-icon-btn delete"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))
  )}
</div>
          </div>

          {deleteConfirm && (
            <div className="chats-history-modal-overlay" onClick={() => setDeleteConfirm(null)}>
              <div className="chats-history-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="chats-history-modal-header">
                  <h3 className="chats-history-modal-title">Delete Conversation?</h3>
                  <p className="chats-history-modal-description">
                    This will permanently delete this conversation. This action cannot be undone.
                  </p>
                </div>

                <div className="chats-history-modal-actions">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="chats-history-modal-button chats-history-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteSession(deleteConfirm);
                    }}
                    className="chats-history-modal-button chats-history-confirm-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {clearAllConfirm && (
            <div className="chats-history-modal-overlay" onClick={() => setClearAllConfirm(null)}>
              <div className="chats-history-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="chats-history-modal-header">
                  <h3 className="chats-history-modal-title">{getClearAllText().title}</h3>
                  <p className="chats-history-modal-description">
                    {getClearAllText().description}
                  </p>
                </div>

                <div className="chats-history-modal-actions">
                  <button
                    onClick={() => setClearAllConfirm(null)}
                    className="chats-history-modal-button chats-history-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearAll();
                    }}
                    className="chats-history-modal-button chats-history-confirm-btn"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Month View
  if (expandedMonth && !expandedWeek && !searchQuery) {
    const weeks = organizedSessions[expandedMonth] || {};
    const now = new Date();
    const currentMonthYear = now.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
    const isCurrentMonth = expandedMonth === currentMonthYear;
    const currentWeekNumber = getCurrentWeekOfMonth();

    const allWeeks = getAllWeeksInMonth(expandedMonth);
    
    Object.keys(weeks).forEach(weekLabel => {
      if (allWeeks[weekLabel]) {
        allWeeks[weekLabel] = weeks[weekLabel];
      }
    });

    const totalSessions = Object.values(weeks).flat().length;

    return (
      <>
        <style>{styles}</style>
        <div className="chats-history-chat-history-container">
          <div className="chats-history-chat-history-inner">
            <div className="chats-history-top-bar">
              <button
                onClick={() => setExpandedMonth(null)}
                className="chats-history-back-button"
              >
                <ChevronLeft size={18} />
              </button>

              {totalSessions > 0 && (
<button
  onClick={() => setClearAllConfirm(true)}
  className="chats-history-clear-all-btn"
>
  Clear {expandedMonth}
</button>
              )}
            </div>

            <div className="chats-history-section-header">
              <h2 className="chats-history-section-title">{expandedMonth}</h2>
            </div>

            <div className="chats-history-folders-grid">
              {Object.entries(allWeeks).map(([weekLabel, sessions], idx) => {
                const color = folderColors[idx % folderColors.length];
                const weekNum = parseInt(weekLabel.split(' ')[1]);
                const isCurrentWeek = isCurrentMonth && weekNum === currentWeekNumber;

                return (
                  <div
                    key={`${expandedMonth}-${weekLabel}`}
                    onClick={() => {
                      if (sessions && sessions.length > 0) {
                        setExpandedWeek(weekLabel);
                      }
                    }}
                    className={`chats-history-folder-card ${
                      isCurrentWeek ? 'chats-history-current-week-highlight' : ''
                    } ${!sessions || sessions.length === 0 ? 'disabled' : ''}`}
                    style={{
                      opacity: !sessions || sessions.length === 0 ? 0.5 : 1,
                      pointerEvents: !sessions || sessions.length === 0 ? 'none' : 'auto',
                      cursor: !sessions || sessions.length === 0 ? 'default' : 'pointer'
                    }}
                  >
                    <div className="chats-history-folder-card-inner">
                      {isCurrentWeek && (
                        <div className="chats-history-current-week-badge">Current Week</div>
                      )}
                      <div className="chats-history-folder-icon-wrapper" style={{ color }}>
                        <FolderIcon color={color} />
                      </div>
                      <div className="chats-history-folder-info">
                        <div className="chats-history-folder-name">{weekLabel}</div>
                        <div className="chats-history-folder-count">
                          {sessions && sessions.length > 0
                            ? `${sessions.length} session${sessions.length !== 1 ? 's' : ''}`
                            : 'No sessions'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {clearAllConfirm && (
            <div className="chats-history-modal-overlay" onClick={() => setClearAllConfirm(null)}>
              <div className="chats-history-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="chats-history-modal-header">
                  <h3 className="chats-history-modal-title">{getClearAllText().title}</h3>
                  <p className="chats-history-modal-description">
                    {getClearAllText().description}
                  </p>
                </div>

                <div className="chats-history-modal-actions">
                  <button
                    onClick={() => setClearAllConfirm(null)}
                    className="chats-history-modal-button chats-history-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearAll();
                    }}
                    className="chats-history-modal-button chats-history-confirm-btn"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Main View
  const showingSearchResults = searchQuery.trim().length > 0;
  const currentMonth = getCurrentMonth();

  return (
    <>
      <style>{styles}</style>
      <div className="chats-history-chat-history-container">
        <div className="chats-history-chat-history-inner">
          <div className="chats-history-header-section">
            <h2 className="chats-history-main-title">Chat History</h2>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              {allSessions.length} total conversation{allSessions.length !== 1 ? 's' : ''}
            </span>
          </div>

          {allSessions.length > 0 && (
            <div className="chats-search-section">
              <div className="chats-search-bar">
                <Search size={18} className="chats-search-icon" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="chats-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="chats-search-clear"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="chats-filters">
                <div className="chats-custom-dropdown" ref={dropdownRef}>
                  <button
                    className="chats-dropdown-button"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    {sortBy === 'recent' ? 'Most Recent' : 
                     sortBy === 'oldest' ? 'Oldest First' :
                     'Most Messages'}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>

                  {showSortDropdown && (
                    <div className="chats-dropdown-menu">
                      {['recent', 'oldest', 'messages'].map(option => (
                        <div
                          key={option}
                          className={`chats-dropdown-item ${sortBy === option ? 'active' : ''}`}
                          onClick={() => {
                            setSortBy(option);
                            setShowSortDropdown(false);
                          }}
                        >
                          {option === 'recent' ? 'Most Recent' :
                           option === 'oldest' ? 'Oldest First' : 'Most Messages'}
                          {sortBy === option && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                  <div className="chats-pin-filter-buttons">
    <button
      className={`chats-filter-chip ${filterPinned === 'all' ? 'active' : ''}`}
      onClick={() => setFilterPinned('all')}
    >
      All
    </button>
    <button
      className={`chats-filter-chip ${filterPinned === 'pinned' ? 'active' : ''}`}
      onClick={() => setFilterPinned('pinned')}
    >
      <Pin size={14} style={{marginRight: '4px'}} />
      Pinned
    </button>
    <button
      className={`chats-filter-chip ${filterPinned === 'unpinned' ? 'active' : ''}`}
      onClick={() => setFilterPinned('unpinned')}
    >
      Unpinned
    </button>
  </div>

                {(searchQuery || allSessions.length > 0) && (
<button
  onClick={() => setClearAllConfirm(true)}
  className="chats-history-clear-all-btn"
>
  {searchQuery ? 'Clear Search Results' : 'Clear All Conversations'}
</button>
                )}
              </div>
            </div>
          )}

          {isLoading && (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading chat history...</p>
            </div>
          )}

          {!isLoading && (
            <>
              {searchQuery ? (
                <div className="search-results">
                  <div className="results-header">
                    <h3>{filteredAndSortedSessions.length} result{filteredAndSortedSessions.length !== 1 ? 's' : ''}</h3>
                  </div>

                  {filteredAndSortedSessions.length === 0 ? (
                    <div className="chats-history-empty-state">
                      <div className="chats-history-empty-icon">🔍</div>
                      <p className="chats-history-empty-text">No conversations found</p>
                    </div>
                  ) : (
                    <div className="chats-history-chat-list-container">
                      {filteredAndSortedSessions.map(session => (
                        <div
                          key={session.id}
                          className={`chats-history-chat-item-card ${session.id === currentSessionId ? 'active' : ''}`}
                          onClick={() => handleSelectSession(session)}
                        >
<div className="chats-history-chat-item-inner">
  <div className="chats-history-chat-preview">
    {session.pinned && <Pin size={14} style={{display: 'inline', marginRight: '8px', color: '#3b82f6'}} />}
    {session.preview || 'New Chat'}
  </div>
  <div className="chats-history-chat-meta">
    <span>{formatTime(session.lastUpdated || session.timestamp)}</span>
    <span className="chats-history-meta-divider">•</span>
    <span>{session.messageCount || 0} msg{session.messageCount !== 1 ? 's' : ''}</span>
  </div>
</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
) : expandedMonth ? (
  null
) : filterPinned === 'pinned' ? (
  <div className="chats-history-chat-list-container">
    {filteredAndSortedSessions.length === 0 ? (
      <div className="chats-history-empty-state">
        <div className="chats-history-empty-icon">📌</div>
        <p className="chats-history-empty-text">No pinned conversations</p>
        <p className="chats-history-empty-subtext">Pin a conversation to find it quickly here</p>
      </div>
    ) : (
      filteredAndSortedSessions.map(session => (
        <div
          key={session.id}
          className={`chats-history-chat-item-card chats-pinned ${session.id === currentSessionId ? 'active' : ''}`}
        >
          <div className="chats-history-chat-item-inner" onClick={() => handleSelectSession(session)}>
            <div className="chats-history-chat-preview">
              <Pin size={14} style={{ display: 'inline', marginRight: '8px', color: '#3b82f6' }} />
              {session.preview || 'New Chat'}
            </div>
            <div className="chats-history-chat-meta">
              <span>{formatTime(session.lastUpdated || session.timestamp)}</span>
              <span className="chats-history-meta-divider">•</span>
              <span>{session.messageCount || 0} msg{session.messageCount !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="chats-action-buttons">
            <button
              onClick={(e) => handleTogglePin(e, session.id)}
              className="chats-icon-btn active"
              title="Unpin"
            >
              <Pin size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDeleteConfirm(session.id); }}
              className="chats-icon-btn delete"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))
    )}
  </div>
) : (
  <div className="chats-history-folders-grid">
    {Object.entries(organizedSessions).map(([monthYear, weeks], idx) => {
      const color = folderColors[idx % folderColors.length];
      const totalSessions = Object.values(weeks).flat().length;
      const isCurrentMonthView = monthYear === currentMonth;

      return (
        <div
          key={`month-${monthYear}`}
          onClick={() => setExpandedMonth(monthYear)}
          className={`chats-history-folder-card ${
            isCurrentMonthView ? 'chats-history-current-month-highlight' : ''
          }`}
        >
          <div className="chats-history-folder-card-inner">
            {isCurrentMonthView && (
              <div className="chats-history-current-month-badge">Current Month</div>
            )}
            <div className="chats-history-folder-icon-wrapper" style={{ color }}>
              <FolderIcon color={color} />
            </div>
            <div className="chats-history-folder-info">
              <div className="chats-history-folder-name">{monthYear}</div>
              <div className="chats-history-folder-count">
                {totalSessions} session{totalSessions !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
)}

              {allSessions.length === 0 && !isLoading && (
                <div className="chats-history-empty-state">
                  <div className="chats-history-empty-icon">💬</div>
                  <p className="chats-history-empty-text">No conversations yet</p>
                  <p className="chats-history-empty-subtext">Start chatting to see your history here</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div className="chats-history-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="chats-history-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chats-history-modal-header">
              <h3 className="chats-history-modal-title">Delete Conversation?</h3>
              <p className="chats-history-modal-description">
                This will permanently delete this conversation. This action cannot be undone.
              </p>
            </div>

            <div className="chats-history-modal-actions">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="chats-history-modal-button chats-history-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteSession(deleteConfirm);
                }}
                className="chats-history-modal-button chats-history-confirm-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {clearAllConfirm && (
        <div className="chats-history-modal-overlay" onClick={() => setClearAllConfirm(null)}>
          <div className="chats-history-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chats-history-modal-header">
              <h3 className="chats-history-modal-title">{getClearAllText().title}</h3>
              <p className="chats-history-modal-description">
                {getClearAllText().description}
              </p>
            </div>

            <div className="chats-history-modal-actions">
              <button
                onClick={() => setClearAllConfirm(null)}
                className="chats-history-modal-button chats-history-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearAll();
                }}
                className="chats-history-modal-button chats-history-confirm-btn"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = `
  * { 
    box-sizing: border-box; 
    margin: 0;
    padding: 0;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .chats-history-chat-history-container {
    height: 100vh;
    overflow-y: auto;
    background: #edededff;
      padding-bottom: 120px; /* ✅ ADD THIS */

    padding: 80px 16px 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .chats-history-chat-history-inner {
    max-width: 880px;
    margin: 0 auto;
  }

  .chats-history-header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .chats-history-main-title {
    font-size: 20px;
    font-weight: 500;
    color: #1f2937;
  }

  .chats-search-section {
    margin-bottom: 20px;
  }

  .chats-search-bar {
    position: relative;
    margin-bottom: 12px;
  }

  .chats-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
  }

  .chats-search-input {
    width: 100%;
    padding: 12px 40px 12px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 28px;
    font-size: 14px;
    background: #ffffff;
    transition: all 0.2s ease;
  }

  .chats-search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .chats-search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .chats-search-clear:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  .chats-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .chats-custom-dropdown {
    position: relative;
  }

  .chats-dropdown-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 24px;
    font-size: 13px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #1f2937;
    font-weight: 500;
    white-space: nowrap;
  }

  .chats-dropdown-button:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
  }

  .chats-dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 160px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 6px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    z-index: 100;
    animation: dropdownFadeIn 0.2s ease;
  }

  .chats-dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
  }

  .chats-dropdown-item:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  .chats-dropdown-item.active {
    background: #eff6ff;
    color: #3b82f6;
    font-weight: 600;
  }

  .chats-history-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .chats-history-back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    color: #000000;
    padding: 10px 10px;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .chats-history-clear-all-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f6f6f6ff;
    border: 1px solid rgba(255, 201, 201, 1);
    color: #ff1515ff;
    padding: 10px 16px;
    border-radius: 28px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }


  .chats-history-section-header {
    margin-bottom: 24px;
  }

  .chats-history-section-title {
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
  }

  .chats-history-folders-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .chats-history-folder-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 5px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .chats-history-folder-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    border-color: #d1d5db;
  }

  .chats-history-folder-card.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .chats-history-folder-card.chats-history-current-month-highlight {
    border: 1px solid #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }

  .chats-history-folder-card.chats-history-current-week-highlight {
    border: 1px solid #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }

  .chats-history-folder-card-inner {
    background: #f9fafb;
    border-radius: 12px;
    padding: 20px;
    position: relative;
  }

  .chats-history-current-month-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: #3b82f6;
    color: white;
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 10;
  }

  .chats-history-current-week-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: #3b82f6;
    color: white;
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 10;
  }

  .chats-history-folder-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }

  .chats-history-folder-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .chats-history-folder-name {
    font-size: 16px;
    font-weight: 600;
    text-align:left;
    color: #1f2937;
  }

  .chats-history-folder-count {
    font-size: 13px;
    color: #6b7280;
        text-align:left;
  }

  .chats-history-chat-list-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .chats-history-chat-item-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .chats-history-chat-item-card.chats-pinned {
    border: 1px solid #d1d5db;
    background: #f9fafb;
  }

  .chats-history-chat-item-card:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .chats-history-chat-item-card:hover .chats-action-buttons {
    opacity: 1;
  }

  .chats-history-chat-item-inner {
    background: #f9fafb;
    border-radius: 9px;
    padding: 16px;
    flex: 1;
    position: relative;
  }

  .chats-history-chat-preview {
    font-size: 15px;
    font-weight: 500;
    color: #1f2937;
    margin-bottom: 8px;
    line-height: 1.5;
    text-align: left;
  }

  .chats-history-chat-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #9ca3af;
  }

  .chats-history-meta-divider {
    color: #d1d5db;
  }

  .chats-action-buttons {
    display: flex;
    gap: 4px;
    padding-right: 8px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .chats-icon-btn {
    background: white;
    border: 1px solid #e5e7eb;
    color: #6b7280;
    padding: 8px;
    border-radius: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }


  .chats-icon-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  .chats-icon-btn.delete {
    color: #ef4444;
  }
  .chats-history-empty-state {
    text-align: center;
    padding: 80px 20px;
    background: #f9fafb;
    border-radius: 20px;
    border: 1px solid #e5e7eb;
  }

  .chats-history-empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .chats-history-empty-text {
    font-size: 16px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 8px;
  }

  .chats-history-empty-subtext {
    font-size: 14px;
    color: #9ca3af;
  }

  .chats-history-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 10002;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { 
      transform: translateY(100%);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }

  .chats-history-modal-content {
    background: #fff;
    border-radius: 24px 24px 0 0;
    width: 100%;
    max-width: 420px;
    padding: 24px;
    box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .chats-history-modal-header {
    margin-bottom: 20px;
  }

  .chats-history-modal-title {
    font-size: 18px;
    font-weight: 400;
    color: #000;
    margin: 0 0 12px 0;
    text-align: left;
  }

  .chats-history-modal-description {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin: 0;
    text-align: left;
  }

  .chats-history-modal-actions {
    display: flex;
    gap: 12px;
  }

  .chats-history-modal-button {
    flex: 1;
    padding: 12px 20px;
    border-radius: 28px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .chats-history-cancel-btn {
    background: #f3f4f6;
    color: #374151;
  }

  .chats-history-confirm-btn {
    background: #000000;
    color: white;
  }


  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .results-header h3 {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  @media (max-width: 768px) {
    .chats-history-chat-history-container {
      padding: 70px 12px 12px;
            padding-bottom: 70px; /* ✅ ADD THIS */

    }

    .chats-history-folders-grid {
      grid-template-columns: 1fr;
    }

    .chats-history-main-title {
      font-size: 18px;
    }

    .chats-history-section-title {
      font-size: 20px;
    }

    .chats-action-buttons {
      opacity: 1;
    }
  }
    .chats-pin-filter-buttons {
  display: flex;
  gap: 6px;
}

.chats-filter-chip {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  font-size: 13px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  font-weight: 500;
}

.chats-filter-chip:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.chats-filter-chip.active {
  background: #eff6ff;
  color: #3b82f6;
  border-color: #3b82f6;
  font-weight: 600;
}

.chats-icon-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}
`;

export default ProfessionalChatHistory;